import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { Submission } from "@/bryntum-lib/models/Submission";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useSubmissions } from "../hooks/useSubmissions";

export type LeadsSourceFilter = "all" | "contact" | "ai_intake";
export type LeadsSortMode = "date_desc" | "date_asc" | "name_asc";

interface LeadsContextValue {
  selectedSubmission: Submission | null;
  toggleSelectedSubmission: (submission: Submission) => void;
  selectSubmission: (submission: Submission) => void;
  clearSelectedSubmission: () => void;
  query: string;
  setQuery: (query: string) => void;
  sortMode: LeadsSortMode;
  setSortMode: (mode: LeadsSortMode) => void;
  sourceFilter: LeadsSourceFilter;
  setSourceFilter: (filter: LeadsSourceFilter) => void;
  clearFilters: () => void;
}

const LeadsContext = createContext<LeadsContextValue | undefined>(undefined);

const DEFAULT_SORT: LeadsSortMode = "date_desc";
const DEFAULT_SOURCE: LeadsSourceFilter = "all";

export function LeadsProvider({ children }: { children: ReactNode }) {
  const { selectedBoard } = useSwivuBryntumContext();
  const { submissions } = useSubmissions();

  const [selectedSubmissionId, setSelectedSubmissionId] = useState<number | null>(null);
  const [query, setQuery] = useState("");
  const [sortMode, setSortMode] = useState<LeadsSortMode>(DEFAULT_SORT);
  const [sourceFilter, setSourceFilter] = useState<LeadsSourceFilter>(DEFAULT_SOURCE);

  const selectedSubmission = useMemo(
    () => submissions.find((s) => Number(s.id) === Number(selectedSubmissionId)) ?? null,
    [submissions, selectedSubmissionId],
  );

  const toggleSelectedSubmission = useCallback((submission: Submission) => {
    setSelectedSubmissionId((prev) =>
      Number(prev) === Number(submission.id) ? null : Number(submission.id),
    );
  }, []);

  const selectSubmission = useCallback((submission: Submission) => {
    setSelectedSubmissionId(Number(submission.id));
  }, []);

  const clearSelectedSubmission = useCallback(() => {
    setSelectedSubmissionId(null);
  }, []);

  const clearFilters = useCallback(() => {
    setQuery("");
    setSortMode(DEFAULT_SORT);
    setSourceFilter(DEFAULT_SOURCE);
  }, []);

  useEffect(() => {
    setSelectedSubmissionId(null);
    setQuery("");
    setSortMode(DEFAULT_SORT);
    setSourceFilter(DEFAULT_SOURCE);
  }, [selectedBoard?.id]);

  const value = useMemo(
    () => ({
      selectedSubmission,
      toggleSelectedSubmission,
      selectSubmission,
      clearSelectedSubmission,
      query,
      setQuery,
      sortMode,
      setSortMode,
      sourceFilter,
      setSourceFilter,
      clearFilters,
    }),
    [
      selectedSubmission,
      toggleSelectedSubmission,
      selectSubmission,
      clearSelectedSubmission,
      query,
      sortMode,
      sourceFilter,
      clearFilters,
    ],
  );

  return <LeadsContext.Provider value={value}>{children}</LeadsContext.Provider>;
}

export function useLeadsContext() {
  const ctx = useContext(LeadsContext);
  if (!ctx) throw new Error("useLeadsContext must be used within LeadsProvider");
  return ctx;
}
